import React, { useState, useEffect } from 'react';
import { DataRetentionApi } from '../../services/ApiService';
import { EventAggregationApi } from '../../services/api/EventAggregationApi';
import { useAuth } from '../../contexts/AuthContext';
import './Settings.css';

/**
 * Reusable card for individual aggregation pipelines
 */
function AggregationCard({ title, description, schedule, status, running, result, error, onExecute }) {
  const staleDays = status?.lastAggregatedAt
    ? Math.floor((Date.now() - new Date(status.lastAggregatedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div style={{
      background: 'var(--card-bg)',
      padding: '1.25rem',
      borderRadius: '12px',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--border-light)',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
    }}>
      <div>
        <h4 style={{ marginTop: 0, marginBottom: '.5rem' }}>{title}</h4>
        <p style={{ color: 'var(--text-secondary)', fontSize: '.8rem', margin: '0 0 .75rem' }}>
          {description} Recommended: {schedule}.
        </p>

        {status && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', marginBottom: '.75rem' }}>
            <div style={{ padding: '.5rem', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '.8rem' }}>
              <div style={{ color: 'var(--text-secondary)' }}>Last Run</div>
              <div style={{ fontWeight: 600 }}>
                {status.lastAggregatedAt
                  ? new Date(status.lastAggregatedAt).toLocaleDateString()
                  : 'Never'}
                {staleDays !== null && staleDays > 3 && (
                  <span style={{ color: '#f59e0b', marginLeft: '.25rem' }}>({staleDays}d ago)</span>
                )}
              </div>
            </div>
            <div style={{ padding: '.5rem', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '.8rem' }}>
              <div style={{ color: 'var(--text-secondary)' }}>Pending</div>
              <div style={{ fontWeight: 600, color: (status.pendingRecords || 0) > 0 ? '#f59e0b' : '#6b7280' }}>
                {(status.pendingRecords || 0).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {status?.error && (
          <div style={{ padding: '.5rem', background: '#fef3c7', borderRadius: '6px', marginBottom: '.5rem', color: '#92400e', fontSize: '.8rem' }}>
            ⚠️ {status.error}
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '.5rem', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', marginBottom: '.5rem', color: '#c00', fontSize: '.8rem' }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ padding: '.5rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '6px', marginBottom: '.5rem', color: '#065f46', fontSize: '.8rem' }}>
          ✅ {(result.recordsAggregated || 0).toLocaleString()} records in {((result.durationMs || 0) / 1000).toFixed(1)}s
        </div>
      )}

      <button
        onClick={onExecute}
        disabled={running}
        style={{
          padding: '.5rem 1rem', fontSize: '.85rem', fontWeight: 600, borderRadius: '8px',
          border: 'none', cursor: running ? 'not-allowed' : 'pointer',
          opacity: running ? 0.6 : 1,
          background: 'var(--primary)', color: '#fff', width: '100%'
        }}
      >
        {running ? '⏳ Running...' : '▶️ Aggregate Now'}
      </button>
    </div>
  );
}

/**
 * DataRetentionSettings
 * Admin page for managing data retention configuration, analytics aggregation, and executing cleanup
 */
export default function DataRetentionSettings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  // Aggregation state
  const [aggregationStatus, setAggregationStatus] = useState(null);
  const [aggregating, setAggregating] = useState(false);
  const [aggregationResult, setAggregationResult] = useState(null);
  const [aggregationError, setAggregationError] = useState(null);
  const [showDateRange, setShowDateRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // GPT aggregation state
  const [gptAggStatus, setGptAggStatus] = useState(null);
  const [gptAggregating, setGptAggregating] = useState(false);
  const [gptAggResult, setGptAggResult] = useState(null);
  const [gptAggError, setGptAggError] = useState(null);

  // Payment aggregation state
  const [paymentAggStatus, setPaymentAggStatus] = useState(null);
  const [paymentAggregating, setPaymentAggregating] = useState(false);
  const [paymentAggResult, setPaymentAggResult] = useState(null);
  const [paymentAggError, setPaymentAggError] = useState(null);

  // Learning session aggregation state
  const [learningAggStatus, setLearningAggStatus] = useState(null);
  const [learningAggregating, setLearningAggregating] = useState(false);
  const [learningAggResult, setLearningAggResult] = useState(null);
  const [learningAggError, setLearningAggError] = useState(null);

  // User profile refresh state
  const [profileRefreshing, setProfileRefreshing] = useState(false);
  const [profileResult, setProfileResult] = useState(null);
  const [profileError, setProfileError] = useState(null);

  // Alert evaluation state
  const [alertEvaluating, setAlertEvaluating] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [alertError, setAlertError] = useState(null);

  // Run-all state
  const [runningAll, setRunningAll] = useState(false);
  const [runAllResult, setRunAllResult] = useState(null);
  const [runAllError, setRunAllError] = useState(null);
  // Cleanup history state
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isAdmin = user?.roles?.some(role => role.roleName === 'admin');

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await DataRetentionApi.getStatus();
      if (!resp.ok) {
        throw new Error(`Failed to fetch status: ${resp.status}`);
      }
      const data = await resp.json();
      setStatus(data);
    } catch (err) {
      setError(err.message || 'Failed to load retention status');
      console.error('Error fetching retention status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAggregationStatus = async () => {
    try {
      const resp = await EventAggregationApi.getAggregationStatus();
      if (!resp.ok) {
        throw new Error(`Failed to fetch aggregation status: ${resp.status}`);
      }
      const data = await resp.json();
      setAggregationStatus(data);
    } catch (err) {
      console.error('Error fetching aggregation status:', err);
      setAggregationError(err.message || 'Failed to load aggregation status');
    }
  };

  const fetchAllAggregationStatuses = async () => {
    // Fetch all pipeline statuses in parallel
    const fetchers = [
      EventAggregationApi.getGptAggregationStatus()
        .then(r => r.ok ? r.json() : null).catch(() => null),
      EventAggregationApi.getPaymentAggregationStatus()
        .then(r => r.ok ? r.json() : null).catch(() => null),
      EventAggregationApi.getLearningSessionAggregationStatus()
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ];
    const [gpt, payment, learning] = await Promise.all(fetchers);
    if (gpt) setGptAggStatus(gpt);
    if (payment) setPaymentAggStatus(payment);
    if (learning) setLearningAggStatus(learning);
  };

  const fetchCleanupHistory = async () => {
    setHistoryLoading(true);
    try {
      const resp = await DataRetentionApi.getCleanupHistory(10);
      if (!resp.ok) {
        throw new Error(`Failed to fetch history: ${resp.status}`);
      }
      const data = await resp.json();
      setCleanupHistory(data);
    } catch (err) {
      console.error('Error fetching cleanup history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStatus();
      fetchAggregationStatus();
      fetchAllAggregationStatuses();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && historyExpanded && cleanupHistory.length === 0) {
      fetchCleanupHistory();
    }
  }, [isAdmin, historyExpanded, cleanupHistory.length]);

  const handleExecuteCleanup = async () => {
    if (!window.confirm(
      `Are you sure you want to execute data retention cleanup?\n\n` +
      `This will permanently delete:\n` +
      `- ${status?.pendingCleanup?.gptPromptEvents || 0} GPT prompt events\n` +
      `- ${status?.pendingCleanup?.genericUsageEvents || 0} generic usage events\n` +
      `- ${status?.pendingCleanup?.orphanedPaymentEvents || 0} orphaned payment events\n\n` +
      `This action cannot be undone!`
    )) {
      return;
    }

    setExecuting(true);
    setError(null);
    setLastResult(null);

    try {
      const resp = await DataRetentionApi.executeCleanup();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Cleanup failed: ${resp.status}`);
      }
      const result = await resp.json();
      setLastResult(result);
      // Refresh status to show updated counts
      await fetchStatus();
      await fetchAggregationStatus();
      await fetchCleanupHistory();
    } catch (err) {
      setError(err.message || 'Failed to execute cleanup');
      console.error('Error executing cleanup:', err);
    } finally {
      setExecuting(false);
    }
  };

  const handleExecuteAggregation = async () => {
    setAggregating(true);
    setAggregationError(null);
    setAggregationResult(null);

    try {
      const params = {};
      if (showDateRange && startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      if (showDateRange && endDate) {
        params.endDate = new Date(endDate).toISOString();
      }

      const resp = await EventAggregationApi.executeAggregation(params);
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Aggregation failed: ${resp.status}`);
      }
      const result = await resp.json();
      setAggregationResult(result);
      // Refresh aggregation status
      await fetchAggregationStatus();
    } catch (err) {
      setAggregationError(err.message || 'Failed to execute aggregation');
      console.error('Error executing aggregation:', err);
    } finally {
      setAggregating(false);
    }
  };

  const handleExecuteGptAggregation = async () => {
    setGptAggregating(true);
    setGptAggError(null);
    setGptAggResult(null);
    try {
      const resp = await EventAggregationApi.executeGptAggregation();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `GPT aggregation failed: ${resp.status}`);
      }
      setGptAggResult(await resp.json());
      await fetchAllAggregationStatuses();
    } catch (err) {
      setGptAggError(err.message || 'Failed to execute GPT aggregation');
    } finally {
      setGptAggregating(false);
    }
  };

  const handleExecutePaymentAggregation = async () => {
    setPaymentAggregating(true);
    setPaymentAggError(null);
    setPaymentAggResult(null);
    try {
      const resp = await EventAggregationApi.executePaymentAggregation();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Payment aggregation failed: ${resp.status}`);
      }
      setPaymentAggResult(await resp.json());
      await fetchAllAggregationStatuses();
    } catch (err) {
      setPaymentAggError(err.message || 'Failed to execute payment aggregation');
    } finally {
      setPaymentAggregating(false);
    }
  };

  const handleExecuteLearningAggregation = async () => {
    setLearningAggregating(true);
    setLearningAggError(null);
    setLearningAggResult(null);
    try {
      const resp = await EventAggregationApi.executeLearningSessionAggregation();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Learning session aggregation failed: ${resp.status}`);
      }
      setLearningAggResult(await resp.json());
      await fetchAllAggregationStatuses();
    } catch (err) {
      setLearningAggError(err.message || 'Failed to execute learning session aggregation');
    } finally {
      setLearningAggregating(false);
    }
  };

  const handleRefreshUserProfiles = async () => {
    setProfileRefreshing(true);
    setProfileError(null);
    setProfileResult(null);
    try {
      const resp = await EventAggregationApi.refreshUserProfiles();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `User profile refresh failed: ${resp.status}`);
      }
      setProfileResult(await resp.json());
    } catch (err) {
      setProfileError(err.message || 'Failed to refresh user profiles');
    } finally {
      setProfileRefreshing(false);
    }
  };

  const handleEvaluateAlerts = async () => {
    setAlertEvaluating(true);
    setAlertError(null);
    setAlertResult(null);
    try {
      const resp = await EventAggregationApi.evaluateAlerts();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Alert evaluation failed: ${resp.status}`);
      }
      setAlertResult(await resp.json());
    } catch (err) {
      setAlertError(err.message || 'Failed to evaluate alerts');
    } finally {
      setAlertEvaluating(false);
    }
  };

  const handleRunAll = async () => {
    setRunningAll(true);
    setRunAllError(null);
    setRunAllResult(null);
    try {
      const resp = await EventAggregationApi.executeAllAggregations();
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || `Run-all failed: ${resp.status}`);
      }
      setRunAllResult(await resp.json());
      // Refresh all statuses
      await fetchAggregationStatus();
      await fetchAllAggregationStatuses();
    } catch (err) {
      setRunAllError(err.message || 'Failed to run all aggregations');
    } finally {
      setRunningAll(false);
    }
  };

  // Calculate staleness (days since last aggregation)
  const calculateStaleness = () => {
    if (!aggregationStatus?.lastAggregatedAt) return null;
    const lastAgg = new Date(aggregationStatus.lastAggregatedAt);
    const now = new Date();
    const diffMs = now - lastAgg;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const staleDays = calculateStaleness();
  const isStale = staleDays !== null && staleDays > 7;

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Data Retention</h2>
        <div className="access-denied">
          <p>Admin access required to view data retention settings.</p>
        </div>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div className="settings-container">
        <h2>Data Retention</h2>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading retention status...</p>
        </div>
      </div>
    );
  }

  const totalPending = status?.pendingCleanup 
    ? (status.pendingCleanup.gptPromptEvents || 0) + 
      (status.pendingCleanup.genericUsageEvents || 0) + 
      (status.pendingCleanup.orphanedPaymentEvents || 0)
    : 0;

  return (
    <div className="settings-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Data Retention</h2>
        <button 
          onClick={fetchStatus} 
          disabled={loading}
          className="btn-secondary"
        >
          {loading ? 'Refreshing...' : '🔄 Refresh Status'}
        </button>
      </div>

      {error && (
        <div style={{
          background: 'var(--color-error-bg, #fee)',
          border: '1px solid var(--color-error-border, #fcc)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#c00'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Staleness Warning */}
      {isStale && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#856404'
        }}>
          <strong>⚠️ Warning:</strong> Analytics data hasn't been aggregated in {staleDays} days. 
          Run aggregation below before executing cleanup to avoid data loss.
        </div>
      )}

      {/* Analytics Aggregation Panel */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-light)',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0 }}>📊 Analytics Aggregation</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '.9rem', marginBottom: '1rem' }}>
          Aggregation pre-computes hourly metrics from raw events for fast analytics queries. 
          <strong> Run aggregation weekly</strong> to keep retention cleanup available and prevent data loss.
        </p>

        {aggregationStatus && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
                Last Aggregated
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                {aggregationStatus.lastAggregatedAt 
                  ? new Date(aggregationStatus.lastAggregatedAt).toLocaleString()
                  : 'Never'}
              </div>
            </div>
            <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
                Pending Events
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: aggregationStatus.pendingRecords > 0 ? '#f59e0b' : '#6b7280' }}>
                {(aggregationStatus.pendingRecords || 0).toLocaleString()}
              </div>
            </div>
            {aggregationStatus.lastAggregationDurationMs && (
              <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
                  Last Duration
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                  {(aggregationStatus.lastAggregationDurationMs / 1000).toFixed(1)}s
                </div>
              </div>
            )}
          </div>
        )}

        {aggregationError && (
          <div style={{ padding: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '1rem', color: '#c00' }}>
            <strong>Error:</strong> {aggregationError}
          </div>
        )}

        {aggregationResult && (
          <div style={{ padding: '1rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', marginBottom: '1rem', color: '#065f46' }}>
            <strong>✅ Aggregation Completed</strong>
            <ul style={{ marginTop: '.5rem', marginBottom: 0, fontSize: '.9rem' }}>
              <li>Records Processed: {(aggregationResult.recordsAggregated || 0).toLocaleString()}</li>
              <li>Metrics Rows: {(aggregationResult.metricsRowsAffected || 0).toLocaleString()}</li>
              <li>Duration: {(aggregationResult.durationMs / 1000).toFixed(1)}s</li>
              {aggregationResult.checkpointUpdated && <li>Checkpoint updated to {new Date(aggregationResult.endDate).toLocaleString()}</li>}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleExecuteAggregation}
            disabled={aggregating}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: aggregating ? 'not-allowed' : 'pointer',
              opacity: aggregating ? 0.6 : 1,
              background: 'var(--primary)',
              color: '#fff'
            }}
          >
            {aggregating ? '⏳ Aggregating...' : '▶️ Aggregate Now (Incremental)'}
          </button>

          <button
            onClick={() => setShowDateRange(!showDateRange)}
            style={{
              padding: '0.75rem 1rem',
              fontSize: '.9rem',
              borderRadius: '8px',
              border: '1px solid var(--border-default)',
              background: 'transparent',
              cursor: 'pointer'
            }}
          >
            {showDateRange ? 'Hide' : 'Show'} Date Range Override
          </button>
        </div>

        {showDateRange && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
            <p style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.75rem' }}>
              Override incremental mode by specifying a custom date range (leave empty for automatic):
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: '.25rem', display: 'block' }}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%', padding: '.5rem', borderRadius: '4px', border: '1px solid var(--border-default)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: '.25rem', display: 'block' }}>
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: '100%', padding: '.5rem', borderRadius: '4px', border: '1px solid var(--border-default)' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Run All Aggregations ────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%)',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '2px solid var(--primary, #007bff)',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0 }}>🚀 Run All Aggregations</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '.9rem', marginBottom: '1rem' }}>
          Runs all 5 aggregation pipelines in sequence: Generic Events → GPT Metrics → Payment Metrics → Learning Sessions → User Profiles.
          Use this when you haven't set up cron jobs and need a single-click full data refresh.
        </p>

        {runAllError && (
          <div style={{ padding: '0.75rem', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '1rem', color: '#c00', fontSize: '.9rem' }}>
            <strong>Error:</strong> {runAllError}
          </div>
        )}

        {runAllResult && (
          <div style={{ padding: '1rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', marginBottom: '1rem', color: '#065f46', fontSize: '.9rem' }}>
            <strong>✅ All Aggregations Completed</strong> in {(runAllResult.totalDurationMs / 1000).toFixed(1)}s
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.5rem', marginTop: '.75rem' }}>
              {['genericEvents', 'gptMetrics', 'paymentMetrics', 'learningSessions', 'userProfiles'].map(key => {
                const r = runAllResult[key];
                const hasError = r?.error;
                return (
                  <div key={key} style={{ padding: '.5rem', background: hasError ? '#fee2e2' : '#ecfdf5', borderRadius: '6px', fontSize: '.8rem' }}>
                    <div style={{ fontWeight: 600 }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                    {hasError 
                      ? <span style={{ color: '#dc2626' }}>❌ {r.error}</span>
                      : <span style={{ color: '#059669' }}>✅ {r.recordsAggregated ?? r.status ?? '—'} processed</span>
                    }
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={handleRunAll}
          disabled={runningAll}
          style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: 700,
            borderRadius: '8px',
            border: 'none',
            cursor: runningAll ? 'not-allowed' : 'pointer',
            opacity: runningAll ? 0.6 : 1,
            background: 'var(--primary)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)'
          }}
        >
          {runningAll ? '⏳ Running All Pipelines...' : '🚀 Run All Aggregations'}
        </button>
      </div>

      {/* ── Individual Aggregation Pipelines ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* GPT Metrics */}
        <AggregationCard
          title="🤖 GPT Metrics"
          description="Aggregates GPT prompt events into gpt_metrics_hourly (model, cost, latency, tokens)."
          schedule="Hourly"
          status={gptAggStatus}
          running={gptAggregating}
          result={gptAggResult}
          error={gptAggError}
          onExecute={handleExecuteGptAggregation}
        />

        {/* Payment Metrics */}
        <AggregationCard
          title="💳 Payment Metrics"
          description="Aggregates payment webhook events into payment_metrics_daily."
          schedule="Daily"
          status={paymentAggStatus}
          running={paymentAggregating}
          result={paymentAggResult}
          error={paymentAggError}
          onExecute={handleExecutePaymentAggregation}
        />

        {/* Learning Sessions */}
        <AggregationCard
          title="📚 Learning Sessions"
          description="Aggregates usage events into learning_session_summary."
          schedule="Daily"
          status={learningAggStatus}
          running={learningAggregating}
          result={learningAggResult}
          error={learningAggError}
          onExecute={handleExecuteLearningAggregation}
        />

        {/* User Profiles */}
        <div style={{
          background: 'var(--card-bg)',
          padding: '1.25rem',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--border-light)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
        }}>
          <div>
            <h4 style={{ marginTop: 0, marginBottom: '.5rem' }}>👤 User Profiles</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '.8rem', margin: '0 0 .75rem' }}>
              Refreshes user_learning_profile materialized view. Recommended: daily.
            </p>
          </div>
          {profileError && (
            <div style={{ padding: '.5rem', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', marginBottom: '.5rem', color: '#c00', fontSize: '.8rem' }}>
              {profileError}
            </div>
          )}
          {profileResult && (
            <div style={{ padding: '.5rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '6px', marginBottom: '.5rem', color: '#065f46', fontSize: '.8rem' }}>
              ✅ Refreshed in {(profileResult.durationMs / 1000).toFixed(1)}s
            </div>
          )}
          <button
            onClick={handleRefreshUserProfiles}
            disabled={profileRefreshing}
            style={{
              padding: '.5rem 1rem', fontSize: '.85rem', fontWeight: 600, borderRadius: '8px',
              border: 'none', cursor: profileRefreshing ? 'not-allowed' : 'pointer',
              opacity: profileRefreshing ? 0.6 : 1,
              background: 'var(--primary)', color: '#fff', width: '100%'
            }}
          >
            {profileRefreshing ? '⏳ Refreshing...' : '▶️ Refresh Now'}
          </button>
        </div>

        {/* Alert Evaluation */}
        <div style={{
          background: 'var(--card-bg)',
          padding: '1.25rem',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--border-light)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
        }}>
          <div>
            <h4 style={{ marginTop: 0, marginBottom: '.5rem' }}>🔔 Alert Evaluation</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '.8rem', margin: '0 0 .75rem' }}>
              Evaluates all active alert rules and fires alerts for threshold breaches. Recommended: every 15 min.
            </p>
          </div>
          {alertError && (
            <div style={{ padding: '.5rem', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', marginBottom: '.5rem', color: '#c00', fontSize: '.8rem' }}>
              {alertError}
            </div>
          )}
          {alertResult && (
            <div style={{ padding: '.5rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '6px', marginBottom: '.5rem', color: '#065f46', fontSize: '.8rem' }}>
              ✅ {alertResult.alertsFired} alerts fired
            </div>
          )}
          <button
            onClick={handleEvaluateAlerts}
            disabled={alertEvaluating}
            style={{
              padding: '.5rem 1rem', fontSize: '.85rem', fontWeight: 600, borderRadius: '8px',
              border: 'none', cursor: alertEvaluating ? 'not-allowed' : 'pointer',
              opacity: alertEvaluating ? 0.6 : 1,
              background: 'var(--primary)', color: '#fff', width: '100%'
            }}
          >
            {alertEvaluating ? '⏳ Evaluating...' : '▶️ Evaluate Now'}
          </button>
        </div>
      </div>

      {/* Configuration */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-light)',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0 }}>Retention Configuration</h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '.85rem' }}>
              Retention Period
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
              {status?.retentionMonths || 6} months
            </div>
            <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginTop: '.25rem' }}>
              Events older than this period are eligible for cleanup
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '.85rem' }}>
              Cutoff Date
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {status?.cutoffDate ? new Date(status.cutoffDate).toLocaleDateString() : 'N/A'}
            </div>
            <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginTop: '.25rem' }}>
              Records before this date will be deleted during cleanup
            </div>
          </div>
          {status?.lastCleanupAt && (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '.85rem' }}>
                Last Cleanup
              </div>
              <div style={{ fontSize: '1rem' }}>
                {new Date(status.lastCleanupAt).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pending Cleanup */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-light)',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0 }}>Records Pending Cleanup</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
              GPT Prompt Events
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: totalPending > 0 ? '#f59e0b' : '#6b7280' }}>
              {(status?.pendingCleanup?.gptPromptEvents || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
              Generic Usage Events
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: totalPending > 0 ? '#f59e0b' : '#6b7280' }}>
              {(status?.pendingCleanup?.genericUsageEvents || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <div style={{ fontSize: '.85rem', color: 'var(--text-secondary)', marginBottom: '.5rem' }}>
              Orphaned Payment Events
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: totalPending > 0 ? '#f59e0b' : '#6b7280' }}>
              {(status?.pendingCleanup?.orphanedPaymentEvents || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '1rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: '#fff' }}>
            <div style={{ fontSize: '.85rem', opacity: 0.9, marginBottom: '.5rem' }}>
              Total Pending
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700 }}>
              {totalPending.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Cleanup Action */}
      <div style={{
        background: totalPending > 0 ? 'var(--color-warning-bg, #fef3c7)' : 'var(--color-bg-subtle)',
        padding: '1.5rem',
        borderRadius: '12px',
        border: `1px solid ${totalPending > 0 ? 'var(--color-warning-border, #fbbf24)' : 'var(--border-default)'}`,
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0, color: totalPending > 0 ? '#92400e' : '#374151' }}>
          Execute Cleanup
        </h3>
        <p style={{ color: totalPending > 0 ? '#78350f' : '#6b7280', marginBottom: '1rem' }}>
          {totalPending > 0
            ? `There are ${totalPending.toLocaleString()} records ready for cleanup. This action is permanent and cannot be undone.`
            : 'No records currently eligible for cleanup.'}
        </p>
        <p style={{ fontSize: '.85rem', color: totalPending > 0 ? '#92400e' : '#6b7280', marginBottom: '1rem' }}>
          ⚠️ <strong>Note:</strong> Payment records are never deleted. Only event tracking data (GPT usage, UI interactions, webhook events) older than {status?.retentionMonths || 6} months will be removed.
        </p>
        <button
          onClick={handleExecuteCleanup}
          disabled={executing || totalPending === 0}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: '8px',
            border: 'none',
            cursor: totalPending === 0 ? 'not-allowed' : 'pointer',
            opacity: totalPending === 0 ? 0.5 : 1,
            background: totalPending > 0 ? 'var(--color-danger, #dc2626)' : 'var(--color-neutral, #9ca3af)',
            color: '#fff',
            boxShadow: totalPending > 0 ? '0 2px 8px rgba(220, 38, 38, 0.3)' : 'none'
          }}
        >
          {executing ? '⏳ Executing Cleanup...' : '🗑️ Execute Cleanup Now'}
        </button>
      </div>

      {/* Last Result */}
      {lastResult && (
        <div style={{
          background: 'var(--color-success-bg, #d1fae5)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid var(--color-success-border, #6ee7b7)',
        }}>
          <h3 style={{ marginTop: 0, color: '#065f46' }}>✅ Cleanup Completed</h3>
          <div style={{ fontSize: '.95rem', color: '#047857' }}>
            <div style={{ marginBottom: '.5rem' }}>
              <strong>Executed at:</strong> {new Date(lastResult.executedAt).toLocaleString()}
            </div>
            <div style={{ marginBottom: '.5rem' }}>
              <strong>Duration:</strong> {lastResult.durationMs}ms
            </div>
            <div style={{ marginTop: '1rem', fontWeight: 600 }}>Records Deleted:</div>
            <ul style={{ marginTop: '.5rem', marginBottom: 0 }}>
              <li>GPT Prompt Events: {(lastResult.deletedRecords?.gptPromptEvents || 0).toLocaleString()}</li>
              <li>Generic Usage Events: {(lastResult.deletedRecords?.genericUsageEvents || 0).toLocaleString()}</li>
              <li>Orphaned Payment Events: {(lastResult.deletedRecords?.orphanedPaymentEvents || 0).toLocaleString()}</li>
              <li><strong>Total: {(lastResult.deletedRecords?.total || 0).toLocaleString()}</strong></li>
            </ul>
          </div>
        </div>
      )}

      {/* Cleanup History */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-light)',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>📜 Cleanup History</h3>
          <button
            onClick={() => {
              setHistoryExpanded(!historyExpanded);
              if (!historyExpanded && cleanupHistory.length === 0) {
                fetchCleanupHistory();
              }
            }}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '.9rem',
              borderRadius: '8px',
              border: '1px solid var(--border-default)',
              background: 'transparent',
              cursor: 'pointer'
            }}
          >
            {historyExpanded ? '▼ Collapse' : '▶ Expand'}
          </button>
        </div>

        {historyExpanded && (
          <>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading history...
              </div>
            ) : cleanupHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                No cleanup operations have been executed yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-default)' }}>
                      <th style={{ padding: '.75rem', textAlign: 'left', fontWeight: 600 }}>Executed At</th>
                      <th style={{ padding: '.75rem', textAlign: 'left', fontWeight: 600 }}>Cutoff Date</th>
                      <th style={{ padding: '.75rem', textAlign: 'right', fontWeight: 600 }}>GPT Events</th>
                      <th style={{ padding: '.75rem', textAlign: 'right', fontWeight: 600 }}>Generic Events</th>
                      <th style={{ padding: '.75rem', textAlign: 'right', fontWeight: 600 }}>Payment Events</th>
                      <th style={{ padding: '.75rem', textAlign: 'right', fontWeight: 600 }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupHistory.map((record) => (
                      <tr key={record.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '.75rem' }}>
                          {new Date(record.executedAt).toLocaleString()}
                        </td>
                        <td style={{ padding: '.75rem' }}>
                          {new Date(record.cutoffDate).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '.75rem', textAlign: 'right' }}>
                          {(record.gptEventsDeleted || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '.75rem', textAlign: 'right' }}>
                          {(record.genericEventsDeleted || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '.75rem', textAlign: 'right' }}>
                          {(record.paymentEventsDeleted || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '.75rem', textAlign: 'right' }}>
                          {record.durationMs ? `${record.durationMs}ms` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Information Panel */}
      <div style={{
        background: 'var(--color-info-bg, #eff6ff)',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '1px solid var(--color-info-border, #bfdbfe)',
        fontSize: '.9rem',
        color: '#1e40af'
      }}>
        <h4 style={{ marginTop: 0, color: '#1e3a8a' }}>ℹ️ About Data Retention & Aggregation</h4>
        <ul style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
          <li>Retention period is configured via the <code>DATA_RETENTION_MONTHS</code> environment variable (default: 6 months)</li>
          <li><strong>Run All Aggregations</strong> or run each pipeline individually to pre-compute analytics metrics</li>
          <li>Cleanup is blocked if attempting to delete non-aggregated data (safety check prevents data loss)</li>
          <li>Only tracking/analytics data is subject to cleanup — never payment records</li>
          <li><strong>6 pipelines:</strong> Generic Events (hourly), GPT Metrics (hourly), Payment Metrics (daily), Learning Sessions (daily), User Profiles (daily), Alert Evaluation (15 min)</li>
          <li>All pipelines are idempotent and safe to re-run — they use ON CONFLICT DO UPDATE</li>
          <li>When cron jobs are set up, these same operations will run automatically via Cloud Scheduler</li>
          <li>Deleted data cannot be recovered — aggregated metrics are preserved</li>
        </ul>
      </div>
    </div>
  );
}
